import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { type Form, type FormAnswer, type ModuleId, type ModuleResult } from '@pixacare/forms';
import { FormBridge } from '@pixacare/forms-angular';
import { FormInteractiveModuleComponent } from '@pixacare/forms-angular/ui';
import { tuiMarkControlAsTouchedAndValidate } from '@taiga-ui/cdk';
import { TuiButton } from '@taiga-ui/core';
import { TuiFade, TuiProgress } from '@taiga-ui/kit';
import { SlideInDirective } from '@modules/shared/directives/slide-in.directive';
import { FileWithModuleId } from '../../models/file-with-module-id';
import { FormOutputs } from '../../models/form-outputs';
import { ModuleResolverInputs } from '../../models/module-resolver-inputs';
import { provideFormDateValueTransformer } from '../../utils/form-date-value-transformer.provider';
import { extractModulePictures, removeModulePictures } from '../../utils/form-modules.utils';
import { FormAutoTriggerComponent } from '../form-auto-trigger/form-auto-trigger.component';
import { FormSectionComponent } from '../form-section/form-section.component';

@Component({
  selector: 'pxc-form',
  templateUrl: './form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TuiButton,
    TuiProgress,
    TuiFade,
    ReactiveFormsModule,
    TranslocoDirective,
    FormSectionComponent,
    FormAutoTriggerComponent,
    FormInteractiveModuleComponent,
    SlideInDirective,
  ],
  providers: [FormBridge, provideFormDateValueTransformer()],
  host: { class: 'flex min-h-0 flex-col' },
})
export class FormComponent implements OnInit {
  private readonly formBridge = inject(FormBridge);

  readonly form = input.required<Form>();
  readonly formAnswer = input<FormAnswer>();
  readonly formInputs = input<ModuleResolverInputs>();
  readonly readOnly = input(false);
  readonly finishText = input<string | null>('Terminer');
  // The questions and the interactive modules slide in when the form appears; its navigation buttons stay in place.
  readonly slideDirection = input(0);

  // Emitted with the filled answer when the user finishes, or with null when they cancel.
  readonly completed = output<FormOutputs | null>();

  outputPictures: FileWithModuleId[] = [];

  readonly formGroup = this.formBridge.formGroup;
  readonly formState = this.formBridge.formState;
  readonly navigationState = this.formBridge.navigationState;
  readonly sections = this.formBridge.sections;
  readonly activeSection = this.formBridge.activeSection;
  readonly activeSectionIndex = this.formBridge.activeSectionIndex;
  readonly sectionColors = this.formBridge.sectionColors;
  readonly isActiveSectionFirst = this.formBridge.isActiveSectionFirst;
  readonly isActiveSectionLast = this.formBridge.isActiveSectionLast;
  readonly isActiveSectionValid = this.formBridge.isActiveSectionValid;
  readonly isFormValid = this.formBridge.isFormValid;
  // Engine-driven AUTO modules to run, rendered by always-mounted form-root
  // triggers so they aren't tied to the active section. See template @for.
  readonly autoModules = this.formBridge.autoModules;
  // Modules the engine keeps alive beside the form for as long as their conditions hold, whatever
  // the active section: rendered in a side column, they write their results straight to the bridge.
  readonly interactiveModules = this.formBridge.interactiveModules;

  ngOnInit(): void {
    this.formBridge.initialize(this.form(), this.formAnswer() ?? { answers: {} }, {
      ignoreDefaultAnswers: this.readOnly(),
    });
  }

  previous(): void {
    if (this.activeSectionIndex() === 0) {
      this.completed.emit(null);
    } else {
      this.formBridge.previousSection();
    }
  }

  next(): void {
    if (!this.isActiveSectionValid()) {
      this.revealSectionErrors(this.activeSectionIndex());
      return;
    }
    this.formBridge.nextSection();
  }

  finish(): void {
    if (!this.isFormValid()) {
      const firstInvalidIndex = this.sections().findIndex((section) => !section.isValid);
      if (firstInvalidIndex !== -1) {
        this.formBridge.navigateTo(firstInvalidIndex);
        this.revealSectionErrors(firstInvalidIndex);
      }
      return;
    }

    const answer = this.formBridge.formAnswer();
    if (!answer?.answers || Object.keys(answer.answers).length === 0) {
      this.completed.emit({ answers: null });
      return;
    }

    const formOutputs: FormOutputs = {
      ...answer,
      pictures: this.outputPictures.length > 0 ? this.outputPictures : undefined,
    };

    this.completed.emit(formOutputs);
  }

  private revealSectionErrors(sectionIndex: number): void {
    const section = this.sections()[sectionIndex];
    if (!section) {
      return;
    }

    this.formBridge.markSectionQuestionsAsVisited(section.id);

    const sectionGroup = this.formGroup()?.get(section.id);
    if (sectionGroup) {
      tuiMarkControlAsTouchedAndValidate(sectionGroup);
    }
  }

  onModuleTriggered(event: { moduleId: ModuleId; targetId: string; result: ModuleResult }): void {
    this.formBridge.applyModuleResult(event.moduleId, event.targetId, event.result);
    const pictures = extractModulePictures(event.moduleId, event.targetId, event.result);
    if (pictures.length === 0) {
      return;
    }
    this.outputPictures = [...this.outputPictures, ...pictures];
    const formInputs = this.formInputs();
    formInputs.files = [...(formInputs.files ?? []), ...pictures];
  }

  onModuleReset(event: { moduleId: ModuleId; targetId: string }): void {
    this.formBridge.resetModuleResult(event.moduleId, event.targetId);
    this.outputPictures = removeModulePictures(this.outputPictures, event.moduleId, event.targetId);
    const formInputs = this.formInputs();
    formInputs.files = removeModulePictures(formInputs.files, event.moduleId, event.targetId);
  }
}
